import { useEffect, useMemo, useState } from 'react';
import {
  dispatch,
  ErrorsRes,
  ErrorVariants,
  getExtraDetails,
  getOrderPricelist,
  getWorkspaceId,
  OrderLine,
  ProductVariant,
  showToast,
  UOM,
  useAddItemToActiveOrderMutation,
  useAppSelector,
  useEditOrderMutation,
  useLazyGetCartQuery,
} from '@zonofi/zono-money-store';
import { OrderProductActionType } from '../../constants';
import { isEmpty } from 'lodash';

export const useAddProductToCreateOrder = (customerId: string) => {
  const customerDetails = useAppSelector(getExtraDetails);
  const workspaceId = useAppSelector(getWorkspaceId);
  const selectedPricelist = useAppSelector(getOrderPricelist);

  const [orderId, setOrderId] = useState<number | null>(null);
  const [addingProductId, setAddingProductId] = useState<number | null>(null);
  const [removeProductId, setRemoveProductId] = useState<number | null>(null);

  const [cartLineByVariantId, setCartLineByVariantId] = useState<
    Map<number, any>
  >(new Map());
  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });
  const [actionType, setActionType] = useState<OrderProductActionType>();

  const [addItemToActiveOrderAPI, addItemToActiveOrderAPIResponse] =
    useAddItemToActiveOrderMutation();
  const [fetchCart, fetchCartRes] = useLazyGetCartQuery();
  const [editRemoveOrderApi, editRemoveOrderResponse] = useEditOrderMutation();

  const fetchCartApi = () => {
    fetchCart({
      workspaceId,
      params: {
        customerId: customerId || '',
        sellerWorkspaceId: workspaceId,
        includeActiveOrders: true,
        includeSummary: true,
      },
    });
  };

  useEffect(() => {
    if (!workspaceId || !customerId) return;
    fetchCartApi();
  }, [workspaceId, customerId]);

  useEffect(() => {
    if (
      addItemToActiveOrderAPIResponse.isSuccess &&
      addItemToActiveOrderAPIResponse?.data
    ) {
      const { orders = [], errors = [] } =
        addItemToActiveOrderAPIResponse?.data;
      setAddingProductId(null);
      setRemoveProductId(null);
      if (orders?.length > 0) {
        fetchCartApi();
        onCloseUomModal();

        dispatch(
          showToast({
            message: 'Product added successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      } else if (errors?.length > 0) {
        dispatch(
          showToast({
            message: errors?.[0]?.msg ?? 'Product not found',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
      }
    } else if (addItemToActiveOrderAPIResponse.isError) {
      setAddingProductId(null);
      setRemoveProductId(null);
      dispatch(
        showToast({
          message: 'Something went wrong',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [addItemToActiveOrderAPIResponse]);

  useEffect(() => {
    if (editRemoveOrderResponse.isSuccess && editRemoveOrderResponse?.data) {
      const { order = {}, errors = [] } = editRemoveOrderResponse?.data;
      setRemoveProductId(null);
      setAddingProductId(null);
      if (!isEmpty(order)) {
        fetchCartApi();
        onCloseUomModal();
        const isRemove = [
          OrderProductActionType.DELETE,
          OrderProductActionType.REMOVE,
        ].includes(actionType as OrderProductActionType);

        dispatch(
          showToast({
            message: `Product ${isRemove ? 'removed' : 'updated'} successfully`,
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      } else if (errors?.length > 0) {
        dispatch(
          showToast({
            message: (errors?.[0] as ErrorsRes)?.msg ?? 'Product not found',
            type: ErrorVariants.DANGER,
            canDisplay: true,
            responseStatus: 'Error',
          })
        );
      }
    } else if (editRemoveOrderResponse.isError) {
      setRemoveProductId(null);
      setAddingProductId(null);
      dispatch(
        showToast({
          message: 'Something went wrong',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [editRemoveOrderResponse]);

  useEffect(() => {
    if (fetchCartRes.isSuccess && fetchCartRes.data) {
      const cartLines = fetchCartRes?.data?.files?.[0]?.lines ?? [];
      const cartLineByVariantId = new Map<number, any>();
      cartLines.forEach((line) => {
        if (line?.productVariantId) {
          cartLineByVariantId.set(line.productVariantId, line);
        }
      });

      setCartLineByVariantId(cartLineByVariantId);
      const orderId = cartLines.find((line) => line?.orderId)?.orderId ?? null;
      setOrderId(orderId);
    }
  }, [fetchCartRes]);

  const onAddProduct = (item: ProductVariant) => {
    if (!item?.productVariantId) return;
    setAddingProductId(item.productVariantId);
    setActionType(OrderProductActionType.ADD);

    addItemToActiveOrderAPI({
      customerId: customerId || '',
      sellerWorkspaceId: workspaceId,
      source: 'platform',
      lines: [
        {
          operator: 'add',
          productVariantId: item?.productVariantId,
          quantity: item?.quantity || 1,
        },
      ],
      ...(selectedPricelist?.pricelistId
        ? { priceListId: selectedPricelist?.pricelistId }
        : {}),
    });
  };

  const handleUpdate = (
    item: ProductVariant,
    type?: OrderProductActionType
  ) => {
    const isRemove = [
      OrderProductActionType.DELETE,
      OrderProductActionType.REMOVE,
    ].includes(type as OrderProductActionType);
    setActionType(type);

    editRemoveOrderApi({
      workspaceId: workspaceId,
      body: {
        customerId: customerId ?? '',
        lines: [
          {
            ...(isRemove ? {} : { operator: 'add' }),
            productVariantId: item?.productVariantId,
            quantity: isRemove ? 0 : item?.newQty || 1,
          },
        ],
        orderId: orderId?.toString(),
      },
    });

    if (isRemove) setRemoveProductId(item.productVariantId);
  };

  const onUomChange = (uom: UOM) => {
    const lineItem = uomModalState.line;
    if (lineItem) {
      addItemToActiveOrderAPI({
        customerId: customerId || '',
        sellerWorkspaceId: workspaceId,
        source: 'platform',
        lines: [
          {
            operator: 'add',
            productVariantId: Number(lineItem?.productVariantId),
            quantity: Number(lineItem.quantity) * uom.packSize,
          },
        ],
      });
    }
  };

  const onOpenUomModal = (cartLine: any) => {
    setUomModalState({ open: true, line: cartLine });
  };

  const onCloseUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const totalCartQuantity = useMemo(() => {
    return Array.from(cartLineByVariantId.values()).reduce(
      (sum, line) => sum + (line?.quantity || 0),
      0
    );
  }, [cartLineByVariantId]);

  return {
    customerName: customerDetails?.customerName,
    addingProductId,
    removeProductId,
    cartLineByVariantId,
    uomModalState,
    totalCartQuantity,
    onUomChange,
    handleUpdate,
    onAddProduct,
    onCloseUomModal,
    onOpenUomModal,
  };
};
